/**
 * FaixasDefesaLeilao.jsx
 * Override das faixas de defesa (fluxo cavalo) específico deste leilão.
 * Lista vazia = usa as faixas padrão do organizador.
 */
import { useState, useEffect } from 'react'
import { RotateCcw } from 'lucide-react'
import toast from 'react-hot-toast'
import faixasDefesaService from '../../services/faixasDefesaService'
import FaixasDefesaEditor from './FaixasDefesaEditor'

export default function FaixasDefesaLeilao({ organizadorId, leilaoId, tipoLeilao }) {
  const [faixas, setFaixas] = useState([])
  const [usandoOverride, setUsandoOverride] = useState(false)
  const [loading, setLoading] = useState(false)
  const [salvando, setSalvando] = useState(false)

  useEffect(() => {
    if (!organizadorId || !leilaoId || tipoLeilao !== 'cavalo') return
    carregar()
  }, [organizadorId, leilaoId, tipoLeilao])

  const carregar = async () => {
    setLoading(true)
    try {
      const override = await faixasDefesaService.listarPorLeilao(organizadorId, leilaoId)
      if (override.length > 0) {
        setFaixas(override)
        setUsandoOverride(true)
      } else {
        const padrao = await faixasDefesaService.listarPadrao(organizadorId)
        setFaixas(padrao)
        setUsandoOverride(false)
      }
    } catch {
      toast.error('Erro ao carregar faixas de defesa')
    } finally {
      setLoading(false)
    }
  }

  const handleSalvar = async () => {
    const invalidas = faixas.some(f => f.valor_fixo === '' || f.valor_fixo === null || f.valor_min === '' || f.valor_min === null)
    if (invalidas) {
      toast.error('Preencha o valor "De" e a "Defesa fixa" em todas as faixas.')
      return
    }
    setSalvando(true)
    try {
      const payload = faixas.map((f, i) => ({
        valor_min: parseFloat(f.valor_min) || 0,
        valor_max: f.valor_max === '' || f.valor_max === null ? null : parseFloat(f.valor_max),
        valor_fixo: parseFloat(f.valor_fixo) || 0,
        ordem: i,
      }))
      const salvas = await faixasDefesaService.salvarPorLeilao(organizadorId, leilaoId, payload)
      setFaixas(salvas)
      setUsandoOverride(true)
      toast.success('Faixas de defesa deste leilão salvas!')
    } catch {
      toast.error('Erro ao salvar faixas de defesa')
    } finally {
      setSalvando(false)
    }
  }

  const handleUsarPadrao = async () => {
    if (!confirm('Remover as faixas customizadas e voltar a usar o padrão do organizador?')) return
    setSalvando(true)
    try {
      await faixasDefesaService.removerOverrideLeilao(organizadorId, leilaoId)
      toast.success('Voltou a usar as faixas padrão do organizador')
      await carregar()
    } catch {
      toast.error('Erro ao remover customização')
    } finally {
      setSalvando(false)
    }
  }

  if (tipoLeilao !== 'cavalo') return null

  return (
    <div className="p-4 bg-amber-50 rounded-lg border border-amber-200 space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-gray-800">🐴 Defesa por Faixa de Valor</h4>
        {usandoOverride && (
          <button onClick={handleUsarPadrao} disabled={salvando}
            className="flex items-center gap-1 text-xs text-amber-700 hover:text-amber-900">
            <RotateCcw className="w-3.5 h-3.5" /> Usar padrão do organizador
          </button>
        )}
      </div>
      <p className="text-sm text-gray-600">
        {usandoOverride
          ? 'Este leilão usa faixas customizadas (diferentes do padrão do organizador).'
          : 'Este leilão usa as faixas padrão do organizador. Edite abaixo para customizar só este leilão.'}
      </p>

      {loading ? (
        <div className="text-center py-3 text-gray-400 text-sm">Carregando...</div>
      ) : (
        <>
          <FaixasDefesaEditor faixas={faixas} onChange={setFaixas} />
          <div className="flex justify-end">
            <button onClick={handleSalvar} disabled={salvando} className="btn-primary text-sm">
              {salvando ? 'Salvando...' : 'Salvar Faixas deste Leilão'}
            </button>
          </div>
        </>
      )}
    </div>
  )
}
